//backend/src/routes/ApplicantRoute.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import API from "../helpers/API";
import Spinner from "../ui/Spinner/Spinner";
import AuthLoader from "../ui/Loader/AuthLoader";

export const ApplicantRoute = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const authContext = useAuth();
  const [auth, setAuth] = authContext;
  const { isAuthenticated, isInitialized, clearAuth } = authContext;
  const navigate = useNavigate();
  const isCheckingRef = useRef(false);
  const hasCheckedRef = useRef(false);

  const checkAuthorization = useCallback(async () => {
    // Wait for auth context to initialize
    if (!isInitialized) {
      console.log("Auth context not initialized yet, waiting...");
      return;
    }

    // Prevent multiple simultaneous authorization checks
    if (isCheckingRef.current) {
      console.log("Authorization check already in progress, skipping...");
      return;
    }

    // authentication check
    if (!isAuthenticated) {
      console.log("User not authenticated, redirecting to login");
      setIsAuthorized(false);
      setIsLoading(false);
      hasCheckedRef.current = true;
      navigate("/Login", { replace: true });
      return;
    }

    isCheckingRef.current = true;

    try {
      console.log(
        "Checking applicant authorization for user:",
        auth.user?.email
      );
      const res = await API.get("/auth/applicantRoute");

      // response validation
      const isValid = res.data && res.data.ok === true;
      console.log("Applicant authorization check result:", isValid);

      setIsAuthorized(isValid);

      //If not authorized, clear auth and redirect
      if (!isValid) {
        console.log(
          "Applicant authorization failed, clearing auth and redirecting"
        );
        clearAuth();
        navigate("/Login", { replace: true });
      }
    } catch (error) {
      console.error("Job Seeker Authorization Check failed:", error);
      setIsAuthorized(false);

      if (error.response?.status === 401) {
        console.log("Token expired or invalid, clearing auth");
        clearAuth();
        navigate("/Login", { replace: true });
      } else if (error.response?.status === 403) {
        console.log("Access denied for current user");
        navigate("/Login", { replace: true });
      } else {
        console.log("Network or server error during applicant auth check");
      }
    } finally {
      setIsLoading(false);
      isCheckingRef.current = false;
      hasCheckedRef.current = true;
    }
  }, [isAuthenticated, isInitialized, auth.user?.email, navigate, clearAuth]);

  useEffect(() => {
    // Only run when context is initialized and check conditions change
    if (isInitialized && (!hasCheckedRef.current || !isAuthenticated)) {
      hasCheckedRef.current = false;
      checkAuthorization();
    }
  }, [isInitialized, isAuthenticated, checkAuthorization]);

  //cleanup on unmount
  useEffect(() => {
    return () => {
      isCheckingRef.current = false;
      hasCheckedRef.current = false;
    };
  }, []);

  // Show loading until auth context is initialized
  if (!isInitialized || isLoading) {
    return <AuthLoader />; // Maintaining consistent loading component
  }

  if (!isAuthenticated) {
    return <Spinner path="/Login" />;
  }

  return isAuthorized ? <Outlet /> : <Spinner path="/Login" />;
};

// export default ApplicantRoute;
